import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { elementClicked, linkClicked } from '../redux/tracking';

import type { SearchTrackingState, TrackClickOptions } from '../types';
import type { Component } from 'react-flow-types';

/** @ignore **/
function mapStateToProps(state: SearchTrackingState): {} {
  return {};
}

/** @ignore **/
const mapDispatchToProps = {
  onElementClick: elementClicked,
  onLinkClick: linkClicked,
};

/** @ignore **/
function getDisplayName(Component): string {
  return Component.displayName || Component.name || 'Component';
}

/**
 * track()
 *
 * This is a HOC that takes a component and wraps it in a connected TrackingLink component.
 *
 * @property {Component} component The component to track.
 * @property {Object} options Options for the tracked component.
 * @property {string} options.id The identifier for this tracked component.
 * @property {Object} options.additionalTrackingProperties More props to append to the tracking call.
 */
export default function track(
  WrappedComponent: Component<any>,
  options?: TrackClickOptions,
): Component<any> {
  /**
   * TrackingLink
   *
   * A simple wrapper around any component, which also dispatches
   * either a LINK_CLICKED or ELEMENT_CLICKED event which other components
   * can respond to - the primary purpose of this is to track
   * entry points to particular URLs and comoponents and where they are located.
   *
   * Configurable prop types are below.
   *
   * @property {string} id The tracked links identifier. This must be unique.
   * @property {string} [href] The href for the link.
   * @property {string} [trackAs] Force to be tracked as either a link or an element
   */

  const { identifier: defaultIdentifier, trackAs } = options || {};

  class TrackingLink extends React.Component {
    static propTypes = {
      // these are set via mapToDispatch
      onLinkClick: PropTypes.func,
      onElementClick: PropTypes.func,
      onClick: PropTypes.func,
      additionalTrackingProps: PropTypes.object,
    };

    click = (event, ...args) => {
      const {
        href,
        onElementClick,
        onLinkClick,
        additionalTrackingProps,
      } = this.props;

      const { target: eventTarget, currentTarget: trackingTarget } = event;

      let { identifier = defaultIdentifier } = this.props;

      if (!identifier && trackingTarget) {
        identifier =
          eventTarget.getAttribute('alt') || trackingTarget.textContent;
      }

      switch (trackAs) {
        case 'link':
          onLinkClick(href, identifier, additionalTrackingProps);
          break;
        case 'element':
          onElementClick(identifier, additionalTrackingProps);
          break;
        default:
          href
            ? onLinkClick(href, identifier, additionalTrackingProps)
            : onElementClick(identifier, additionalTrackingProps);
      }

      if (this.props.onClick) {
        return this.props.onClick(event, ...args);
      }
    };

    render() {
      const {
        identifier,
        onClick,
        onElementClick,
        onLinkClick,
        ...childProps
      } = this.props; /* eslint-disable-line no-unused-vars */

      delete childProps.additionalTrackingProps;

      return <WrappedComponent onClick={this.click} {...childProps} />;
    }
  }

  TrackingLink.displayName = `TrackingLink(${getDisplayName(
    WrappedComponent,
  )})`;

  const ConnectedTrackingLink = connect(
    mapStateToProps,
    mapDispatchToProps,
  )(TrackingLink);

  return ConnectedTrackingLink;
}
